import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Search, HelpCircle, Users, Cpu, LineChart, Shield, BookOpen } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { usePricingSettings, formatDeadline, formatPrice, isDiscountActive } from '../hooks/useDiscountDeadline';

const FAQPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { discount_deadline: discountDeadline, original_price: faqOriginalPrice, current_price: faqCurrentPrice } = usePricingSettings();
  const faqSavings = faqOriginalPrice - faqCurrentPrice;
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  // SEO-optimized FAQ data for AI in HR
  const faqCategories = {
    general: {
      icon: HelpCircle,
      title: language === 'nl' ? 'Algemeen' : 'General',
      questions: [
        {
          q: language === 'nl' 
            ? 'Wat is AI in HR en waarom is het belangrijk?' 
            : 'What is AI in HR and why is it important?',
          a: language === 'nl'
            ? 'AI in HR verwijst naar het gebruik van kunstmatige intelligentie om HR-processen te automatiseren en te verbeteren. Dit omvat werving, onboarding, performance management, en employee engagement. AI helpt HR-teams om data-gedreven beslissingen te nemen, repetitieve taken te automatiseren, en de employee experience te personaliseren. Organisaties die AI succesvol implementeren zien gemiddeld 30-40% efficiëntiewinst in HR-processen.'
            : 'AI in HR refers to the use of artificial intelligence to automate and improve HR processes. This includes recruitment, onboarding, performance management, and employee engagement. AI helps HR teams make data-driven decisions, automate repetitive tasks, and personalize the employee experience. Organizations that successfully implement AI see an average 30-40% efficiency gain in HR processes.'
        },
        {
          q: language === 'nl'
            ? 'Hoe meet ik de AI-maturiteit van mijn HR-afdeling?'
            : 'How do I measure the AI for HR of my HR department?',
          a: language === 'nl'
            ? 'De AI-maturiteit wordt gemeten aan de hand van vier dimensies: Strategie (is er een duidelijke AI-visie?), Governance (zijn er beleid en ethische richtlijnen?), Technologie & Data (is de infrastructuur klaar voor AI?), en Mensen & Change (is het team bereid en bekwaam?). Onze gratis AI for HR Quickscan meet deze dimensies en vergelijkt uw score met de sector benchmark. De quickscan duurt slechts 10 minuten en geeft direct inzicht.'
            : 'AI for HR is measured across four dimensions: Strategy (is there a clear AI vision?), Governance (are there policies and ethical guidelines?), Technology & Data (is the infrastructure ready for AI?), and People & Change (is the team willing and capable?). Our free AI for HR Quickscan measures these dimensions and compares your score with the industry benchmark. The quickscan takes only 10 minutes and provides immediate insights.'
        },
        {
          q: language === 'nl'
            ? 'Is de AI for HR Quickscan echt gratis?'
            : 'Is the AI for HR Quickscan really free?',
          a: language === 'nl'
            ? 'Ja, de quickscan is volledig gratis en vrijblijvend. U ontvangt direct uw resultaten, inclusief een vergelijking met de sector benchmark, gepersonaliseerde aanbevelingen, en toegang tot onze Use Case Hub. Er zijn geen verborgen kosten of verplichtingen.'
            : 'Yes, the quickscan is completely free with no obligations. You receive your results immediately, including a comparison with the industry benchmark, personalized recommendations, and access to our Use Case Hub. There are no hidden costs or obligations.'
        }
      ]
    },
    implementation: {
      icon: Cpu,
      title: language === 'nl' ? 'Implementatie' : 'Implementation',
      questions: [
        {
          q: language === 'nl'
            ? 'Hoe begin ik met AI in HR?'
            : 'How do I get started with AI in HR?',
          a: language === 'nl'
            ? 'Begin met een quickscan van uw huidige situatie. Identificeer vervolgens "quick wins" - AI-toepassingen met lage complexiteit en hoge impact, zoals CV-screening of FAQ-chatbots. Start klein met een pilot, meet de resultaten, en schaal op basis van succes. Onze Use Case Hub bevat praktische voorbeelden gesorteerd op complexiteit en ROI.'
            : 'Start with a quickscan of your current situation. Then identify "quick wins" - AI applications with low complexity and high impact, such as CV screening or FAQ chatbots. Start small with a pilot, measure results, and scale based on success. Our Use Case Hub contains practical examples sorted by complexity and ROI.'
        },
        {
          q: language === 'nl'
            ? 'Welke AI use cases zijn het meest geschikt voor beginners?'
            : 'Which AI use cases are most suitable for beginners?',
          a: language === 'nl'
            ? 'Voor organisaties die net beginnen met AI in HR raden we aan: 1) CV-screening en candidate matching (automatisch relevante kandidaten identificeren), 2) Chatbots voor veelgestelde vragen (24/7 ondersteuning voor medewerkers), 3) Geautomatiseerde onboarding flows (consistent en persoonlijk), 4) Sentiment analyse van medewerkerfeedback. Deze use cases hebben een bewezen ROI en zijn relatief eenvoudig te implementeren.'
            : 'For organizations just starting with AI in HR, we recommend: 1) CV screening and candidate matching (automatically identify relevant candidates), 2) Chatbots for FAQs (24/7 support for employees), 3) Automated onboarding flows (consistent and personal), 4) Sentiment analysis of employee feedback. These use cases have proven ROI and are relatively easy to implement.'
        },
        {
          q: language === 'nl'
            ? 'Hoe lang duurt een typische AI-implementatie in HR?'
            : 'How long does a typical AI implementation in HR take?',
          a: language === 'nl'
            ? 'Dit varieert sterk afhankelijk van de complexiteit. Een eenvoudige chatbot kan binnen 4-6 weken live zijn. Een volledig geïntegreerd AI-recruitment systeem duurt typisch 3-6 maanden. Factoren die de tijdlijn beïnvloeden zijn: datakwaliteit, systeemintegraties, change management, en beschikbare resources. We raden aan om te starten met een 8-12 weken pilot voor uw eerste use case.'
            : 'This varies greatly depending on complexity. A simple chatbot can be live within 4-6 weeks. A fully integrated AI recruitment system typically takes 3-6 months. Factors affecting the timeline include: data quality, system integrations, change management, and available resources. We recommend starting with an 8-12 week pilot for your first use case.'
        }
      ]
    },
    roi: {
      icon: LineChart,
      title: 'ROI & Business Case',
      questions: [
        {
          q: language === 'nl'
            ? 'Wat is de typische ROI van AI in HR?'
            : 'What is the typical ROI of AI in HR?',
          a: language === 'nl'
            ? 'De ROI varieert per use case, maar typische resultaten zijn: 40-60% tijdsbesparing bij CV-screening, 25-35% reductie in time-to-hire, 20-30% verbetering in employee engagement bij gepersonaliseerde learning, en 50-70% reductie in HR-servicekosten door chatbots. Onze ROI Calculator kan een schatting maken voor uw specifieke situatie.'
            : 'ROI varies by use case, but typical results include: 40-60% time savings in CV screening, 25-35% reduction in time-to-hire, 20-30% improvement in employee engagement with personalized learning, and 50-70% reduction in HR service costs through chatbots. Our ROI Calculator can estimate results for your specific situation.'
        },
        {
          q: language === 'nl'
            ? 'Hoe bouw ik een business case voor AI in HR?'
            : 'How do I build a business case for AI in HR?',
          a: language === 'nl'
            ? 'Een sterke business case bevat: 1) Huidige kosten en inefficiënties (tijd, geld, fouten), 2) Verwachte besparingen en verbeteringen, 3) Implementatiekosten (technologie, training, change management), 4) Tijdlijn tot ROI, 5) Risico\'s en mitigatie strategieën, 6) Vergelijking met alternatieven. Wij helpen graag bij het opstellen van uw business case tijdens een gratis consultatie.'
            : 'A strong business case includes: 1) Current costs and inefficiencies (time, money, errors), 2) Expected savings and improvements, 3) Implementation costs (technology, training, change management), 4) Timeline to ROI, 5) Risks and mitigation strategies, 6) Comparison with alternatives. We are happy to help you build your business case during a free consultation.'
        }
      ]
    },
    ethics: {
      icon: Shield,
      title: language === 'nl' ? 'Ethiek & Compliance' : 'Ethics & Compliance',
      questions: [
        {
          q: language === 'nl'
            ? 'Hoe zorg ik voor ethische AI in HR?'
            : 'How do I ensure ethical AI in HR?',
          a: language === 'nl'
            ? 'Ethische AI in HR vereist: 1) Transparantie - medewerkers weten wanneer AI wordt gebruikt, 2) Eerlijkheid - regelmatige bias audits van algoritmes, 3) Privacy - naleving van GDPR en andere regelgeving, 4) Menselijke controle - AI ondersteunt, maar vervangt niet de menselijke besluitvorming, 5) Accountability - duidelijke verantwoordelijkheden voor AI-beslissingen. Wij helpen bij het opstellen van AI governance frameworks.'
            : 'Ethical AI in HR requires: 1) Transparency - employees know when AI is being used, 2) Fairness - regular bias audits of algorithms, 3) Privacy - compliance with GDPR and other regulations, 4) Human oversight - AI supports but does not replace human decision-making, 5) Accountability - clear responsibilities for AI decisions. We help establish AI governance frameworks.'
        },
        {
          q: language === 'nl'
            ? 'Voldoet AI in HR aan de GDPR/AVG?'
            : 'Does AI in HR comply with GDPR?',
          a: language === 'nl'
            ? 'AI-systemen kunnen GDPR-compliant zijn, maar dit vereist aandacht voor: gegevensverzameling en -verwerking, transparantie over automatische besluitvorming, recht op uitleg bij geautomatiseerde beslissingen, data minimalisatie, en beveiligingsmaatregelen. Het is essentieel om een Data Protection Impact Assessment (DPIA) uit te voeren voor AI-implementaties die persoonsgegevens verwerken.'
            : 'AI systems can be GDPR compliant, but this requires attention to: data collection and processing, transparency about automated decision-making, right to explanation for automated decisions, data minimization, and security measures. It is essential to conduct a Data Protection Impact Assessment (DPIA) for AI implementations that process personal data.'
        }
      ]
    },
    workshops: {
      icon: Users,
      title: language === 'nl' ? 'Workshops & Training' : 'Workshops & Training',
      questions: [
        {
          q: language === 'nl'
            ? 'Wat kan ik verwachten van een AI Workshop?'
            : 'What can I expect from an AI Workshop?',
          a: language === 'nl'
            ? 'Onze workshops zijn praktijkgericht en interactief. In een sessie van 2-4 uur leert uw team: de basis van AI in HR, hoe AI-tools te evalueren, hands-on oefeningen met AI-toepassingen, en hoe een AI-roadmap op te stellen. Workshops zijn beschikbaar voor 4-8 deelnemers en worden gehouden in Antwerpen, Gent of Brussel. Na de workshop ontvangt elk team een actieplan.'
            : 'Our workshops are practical and interactive. In a 2-4 hour session, your team learns: the basics of AI in HR, how to evaluate AI tools, hands-on exercises with AI applications, and how to create an AI roadmap. Workshops are available for 4-8 participants and are held in Antwerp, Ghent or Brussels. After the workshop, each team receives an action plan.'
        },
        {
          q: language === 'nl'
            ? 'Wat kosten de workshops?'
            : 'What do the workshops cost?',
          a: language === 'nl'
            ? (isDiscountActive(discountDeadline)
              ? `Onze AI in HR workshops kosten normaal ${formatPrice(faqOriginalPrice, 'nl')} voor 5-15 deelnemers. Momenteel bieden we een introductieprijs van ${formatPrice(faqCurrentPrice, 'nl')} aan (bespaar ${formatPrice(faqSavings, 'nl')}!). Deze actie is geldig tot ${formatDeadline(discountDeadline, 'nl')}. De prijs omvat een ervaren facilitator, hands-on materiaal, en een gepersonaliseerd actieplan. Bekijk onze workshop pagina voor meer details en om een sessie te boeken.`
              : `Onze AI in HR workshops kosten ${formatPrice(faqOriginalPrice, 'nl')} voor 5-15 deelnemers. De prijs omvat een ervaren facilitator, hands-on materiaal, en een gepersonaliseerd actieplan. Bekijk onze workshop pagina voor meer details en om een sessie te boeken.`)
            : (isDiscountActive(discountDeadline)
              ? `Our AI in HR workshops normally cost ${formatPrice(faqOriginalPrice, 'en')} for 5-15 participants. Currently we offer an introductory price of ${formatPrice(faqCurrentPrice, 'en')} (save ${formatPrice(faqSavings, 'en')}!). This offer is valid until ${formatDeadline(discountDeadline, 'en')}. The price includes an experienced facilitator, hands-on materials, and a personalized action plan. Check our workshop page for more details and to book a session.`
              : `Our AI in HR workshops cost ${formatPrice(faqOriginalPrice, 'en')} for 5-15 participants. The price includes an experienced facilitator, hands-on materials, and a personalized action plan. Check our workshop page for more details and to book a session.`)
        },
        {
          q: language === 'nl'
            ? 'Waar worden de workshops gehouden?'
            : 'Where are the workshops held?',
          a: language === 'nl'
            ? 'Onze workshops worden gehouden op locaties in Antwerpen, Gent en Brussel. We komen ook naar uw kantoor voor in-house sessies. Online workshops zijn eveneens beschikbaar. Groepsgrootte is ideaal 4-8 deelnemers voor optimale interactie en hands-on begeleiding.'
            : 'Our workshops are held at locations in Antwerp, Ghent and Brussels. We also come to your office for in-house sessions. Online workshops are also available. Group size is ideally 4-8 participants for optimal interaction and hands-on guidance.'
        }
      ]
    },
    resources: {
      icon: BookOpen,
      title: language === 'nl' ? 'Resources' : 'Resources',
      questions: [
        {
          q: language === 'nl'
            ? 'Wat bevat de Use Case Hub?'
            : 'What does the Use Case Hub contain?',
          a: language === 'nl'
            ? 'De Use Case Hub is een bibliotheek van praktische AI-toepassingen voor HR. Elke use case bevat: een beschrijving van het probleem en de oplossing, verwachte ROI en implementatietijd, vereiste technologie en vaardigheden, praktijkvoorbeelden, en tips voor implementatie. Use cases zijn gefilterd op categorie (recruitment, learning, engagement, etc.), complexiteit, en impact.'
            : 'The Use Case Hub is a library of practical AI applications for HR. Each use case contains: a description of the problem and solution, expected ROI and implementation time, required technology and skills, practical examples, and implementation tips. Use cases are filtered by category (recruitment, learning, engagement, etc.), complexity, and impact.'
        },
        {
          q: language === 'nl'
            ? 'Hoe kan ik op de hoogte blijven van AI in HR trends?'
            : 'How can I stay updated on AI in HR trends?',
          a: language === 'nl'
            ? 'Volg onze blog voor regelmatige updates over AI in HR. Schrijf u in voor onze nieuwsbrief voor maandelijkse inzichten. Volg ons op LinkedIn voor dagelijkse tips. En neem deel aan onze gratis workshops voor hands-on ervaring met de nieuwste tools.'
            : 'Follow our blog for regular updates on AI in HR. Subscribe to our newsletter for monthly insights. Follow us on LinkedIn for daily tips. And participate in our free workshops for hands-on experience with the latest tools.'
        }
      ]
    }
  };

  const toggleItem = (categoryKey, index) => {
    const key = `${categoryKey}-${index}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = Object.entries(faqCategories).filter(([key, category]) => {
    if (activeCategory !== 'all' && key !== activeCategory) return false;
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return category.questions.some(q => 
      q.q.toLowerCase().includes(searchLower) || 
      q.a.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'nl' ? 'Terug' : 'Back'}
          </button>

          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'nl' ? 'Veelgestelde Vragen over AI in HR' : 'Frequently Asked Questions about AI in HR'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Antwoorden op de meest gestelde vragen over kunstmatige intelligentie in Human Resources'
                : 'Answers to the most common questions about artificial intelligence in Human Resources'}
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={language === 'nl' ? 'Zoek in FAQ...' : 'Search FAQ...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {language === 'nl' ? 'Alles' : 'All'}
            </button>
            {Object.entries(faqCategories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeCategory === key 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <category.icon className="h-4 w-4" />
                {category.title}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-6">
            {filteredCategories.map(([categoryKey, category]) => (
              <div key={categoryKey} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <category.icon className="h-5 w-5 text-blue-500" />
                    <h2 className="text-lg font-bold text-gray-900">{category.title}</h2>
                  </div>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {category.questions
                    .filter(q => !searchTerm || 
                      q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      q.a.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((item, index) => {
                      const key = `${categoryKey}-${index}`;
                      const isOpen = openItems[key];
                      
                      return (
                        <div key={item.q} className="px-6">
                          <button
                            onClick={() => toggleItem(categoryKey, index)}
                            className="w-full py-4 flex items-start justify-between text-left"
                          >
                            <span className="font-medium text-gray-900 pr-4">{item.q}</span>
                            {isOpen ? (
                              <ChevronUp className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="pb-4">
                              <p className="text-gray-600 leading-relaxed">{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-blue-500 to-blue-700 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">
              {language === 'nl' ? 'Nog vragen?' : 'Still have questions?'}
            </h3>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              {language === 'nl'
                ? 'Neem contact met ons op voor een gratis consultatie. We helpen u graag verder met uw AI in HR strategie.'
                : 'Contact us for a free consultation. We\'re happy to help you with your AI in HR strategy.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                {language === 'nl' ? 'Neem Contact Op' : 'Contact Us'}
              </a>
              <a
                href="/quickscan/start"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors border border-blue-400"
              >
                {language === 'nl' ? 'Start Gratis Quickscan' : 'Start Free Quickscan'}
              </a>
            </div>
          </div>

          {/* Schema.org FAQ structured data for SEO */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": Object.values(faqCategories).flatMap(category =>
                category.questions.map(q => ({
                  "@type": "Question",
                  "name": q.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": q.a
                  }
                }))
              )
            })
          }} />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQPage;
